import * as WEBGPU from "three/webgpu";
import { bloom } from "three/addons/tsl/display/BloomNode.js";
import { float, pass, vec4 } from "three/tsl";
import type { NexusEvent } from "../../domain/mesh";
import type { PresentedSnapshot } from "../../presentation/mesh";
import type { FocusState } from "../../store/nexusStore";
import { semanticZoomState, type SemanticZoomState } from "../../experience/semanticZoom";
import { focusedNodeId, focusedOrbitalId } from "../../store/nexusStore";
import { blackHoleConfig } from "../config/blackHoleConfig";
import { ProceduralCelestialEnvironment } from "../environment/ProceduralCelestialEnvironment";
import { RareCelestialEventSystem } from "../environment/RareCelestialEventSystem";
import { PointerController } from "../interaction/PointerController";
import { relationPhysics } from "../relations/relationPhysics";
import { WorldScene } from "../scene/WorldScene";
import { RaymarchFieldProjector } from "../spatial/RaymarchFieldProjector";
import { BlackHoleSimulation } from "../vendor/dgreenheck/blackhole.js";
import { AdaptiveRenderScaleController } from "./AdaptiveRenderScaleController";
import { CameraDirector } from "./CameraDirector";
import type { EngineCallbacks } from "./engineTypes";
import { LabelLayer } from "./LabelLayer";

type BlackHoleConnectorField = {
  start: [number, number];
  end: [number, number];
  thickness: number;
  opacity: number;
  brightness: number;
  curvature: number;
};

export class NexusEngine {
  private readonly scene = new WEBGPU.Scene();
  private readonly labelScene = new WEBGPU.Scene();
  private readonly cameraDirector: CameraDirector;
  private readonly world: WorldScene;
  private readonly labels: LabelLayer;
  private readonly environment: ProceduralCelestialEnvironment;
  private readonly rareEvents: RareCelestialEventSystem;
  private readonly interactive: WEBGPU.Object3D[] = [];
  private renderer: WEBGPU.WebGPURenderer | null = null;
  private postProcessing: WEBGPU.PostProcessing | null = null;
  private blackHole: BlackHoleSimulation | null = null;
  private pointer: PointerController | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private frameId = 0;
  private running = false;
  private lastTime = 0;
  private elapsed = 0;
  private width = 1;
  private height = 1;
  private focus: FocusState = { kind: "cosmos" };
  private hoveredEntityId: string | null = null;
  private snapshotKey = "";
  private snapshot: PresentedSnapshot | null = null;
  private activePulse: NexusEvent | null = null;
  private reducedMotion = false;
  private accessibleMode = false;
  private demoTourActive = false;
  private appliedViewportWidth = 0;
  private appliedViewportHeight = 0;
  private appliedPixelRatio = 0;
  private semanticZoom: SemanticZoomState = { level: 0, blend: 0, distance: 44 };
  private idleMode = false;
  private exposure = 1;
  private interactionPaddingScale = 1;
  private readonly raymarchField = new RaymarchFieldProjector();
  private readonly renderScaleController = new AdaptiveRenderScaleController();
  private focusArchetype: "stellar" | "black-hole" = "stellar";

  constructor(
    private readonly host: HTMLDivElement,
    private readonly callbacks: EngineCallbacks,
  ) {
    this.scene.background = new WEBGPU.Color(0x000000);
    this.labelScene.background = null;
    this.cameraDirector = new CameraDirector(this.host.clientWidth / Math.max(this.host.clientHeight, 1));
    this.world = new WorldScene(this.scene);
    this.labels = new LabelLayer(this.labelScene);
    this.environment = new ProceduralCelestialEnvironment(this.scene);
    this.rareEvents = new RareCelestialEventSystem(this.scene);
    this.host.dataset.targetFps = "60";
  }

  async init(signal?: AbortSignal) {
    if (signal?.aborted) return;
    const renderer = new WEBGPU.WebGPURenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.domElement.className = "constellation-canvas";
    renderer.domElement.setAttribute("aria-hidden", "true");
    this.host.prepend(renderer.domElement);
    await renderer.init();
    if (signal?.aborted) {
      renderer.dispose();
      renderer.domElement.remove();
      return;
    }

    renderer.setClearColor(0x000000, 0);
    renderer.toneMapping = WEBGPU.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    this.renderer = renderer;
    this.blackHole = new BlackHoleSimulation(this.scene, blackHoleConfig);
    this.blackHole.createBlackHole();
    if (this.snapshot) this.syncBlackHoleFieldGeometry();

    const postProcessing = new WEBGPU.PostProcessing(renderer);
    const scenePass = pass(this.scene, this.cameraDirector.camera);
    const sceneColor = scenePass.getTextureNode();
    const bloomPass = bloom(sceneColor);
    bloomPass.threshold.value = blackHoleConfig.bloomThreshold;
    bloomPass.strength.value = blackHoleConfig.bloomStrength;
    bloomPass.radius.value = blackHoleConfig.bloomRadius;

    // Keep labels out of bloom without issuing a second canvas render. With WebGPU,
    // mixing PostProcessing.render() and a follow-up renderer.render() that loads the
    // previous canvas contents can retain frame history and produce visible trails.
    // The transparent label pass is therefore alpha-composited inside the same TSL
    // post-processing graph, so every animation frame has one canonical canvas write.
    const labelPass = pass(this.labelScene, this.cameraDirector.camera);
    const labelColor = labelPass.getTextureNode();
    const worldColor = sceneColor.add(bloomPass);
    const labelInverseAlpha = float(1).sub(labelColor.a);
    postProcessing.outputNode = vec4(worldColor.rgb.mul(labelInverseAlpha).add(labelColor.rgb), 1);
    this.postProcessing = postProcessing;

    this.pointer = new PointerController(
      renderer.domElement,
      this.cameraDirector.camera,
      this.cameraDirector,
      this.callbacks,
      () => this.interactive,
      (screenX, screenY) => this.labels.pick(screenX, screenY, this.interactionPaddingScale),
      (screenX, screenY) => {
        const singularityId = this.world.pickSingularity(
          this.cameraDirector.camera,
          this.width,
          this.height,
          screenX,
          screenY,
        );
        return singularityId ? { entityId: singularityId, entityKind: "node", parentId: null } : null;
      },
    );

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host);
    this.resize();
    this.host.dataset.engineReady = "true";
    this.running = true;
    this.lastTime = performance.now();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.frameId = requestAnimationFrame(this.frame);
  }

  setSnapshot(snapshot: PresentedSnapshot | null) {
    this.snapshot = snapshot;
    if (!snapshot) return;
    const nextKey = `${snapshot.revision}:${snapshot.nodes.map((node) => node.id).join(",")}:${snapshot.orbitalNodes.map((orbital) => orbital.id).join(",")}`;
    if (nextKey !== this.snapshotKey) {
      this.snapshotKey = nextKey;
      this.world.rebuild(snapshot);
      this.labels.rebuild(snapshot.nodes, snapshot.orbitalNodes, this.world.visualAnchors);
      this.syncInteractive();
      this.syncBlackHoleFieldGeometry();
      this.applyFocus();
      if (this.activePulse) this.world.setPulse(this.activePulse);
      return;
    }
    this.labels.updateCopy(snapshot.nodes, snapshot.orbitalNodes);
  }

  setFocus(focus: FocusState) {
    this.focus = focus;
    this.world.setFocus(focus);
    this.applyFocus();
  }

  setHovered(entityId: string | null) {
    this.hoveredEntityId = entityId;
    this.world.setHovered(entityId);
  }

  setPulse(event: NexusEvent | null) {
    this.activePulse = event;
    this.world.setPulse(event);
  }

  setReducedMotion(reduced: boolean) {
    this.reducedMotion = reduced;
    this.cameraDirector.setReducedMotion(reduced);
    this.world.setReducedMotion(reduced);
    this.environment.setReducedMotion(reduced);
    this.rareEvents.setReducedMotion(reduced);
  }

  setAccessibleMode(enabled: boolean) {
    this.accessibleMode = enabled;
    this.cameraDirector.setAccessibleMode(enabled);
    this.world.setAccessibleMode(enabled);
    this.environment.setAccessibleMode(enabled);
    this.rareEvents.setAccessibleMode(enabled);
  }

  setDemoTourActive(active: boolean) {
    this.demoTourActive = active;
    this.cameraDirector.setDemoTourActive(active);
  }

  setIdleMode(idle: boolean) {
    this.idleMode = idle;
    this.cameraDirector.setIdleMode(idle);
  }

  setDisplayRefreshHz(displayRefreshHz: number) {
    this.renderScaleController.setTargetRefreshHz(displayRefreshHz);
    this.host.dataset.targetFps = String(displayRefreshHz);
  }

  private applyFocus() {
    const position = this.world.getSemanticFocusPosition(this.focus);
    this.focusArchetype = this.world.getFocusArchetype(this.focus);
    const frame = this.world.getFocusFrame(this.focus);
    this.cameraDirector.setFocus(this.focus, position, this.focusArchetype, frame);
  }

  private syncInteractive() {
    this.interactive.length = 0;
    this.interactive.push(...this.world.interactive);
  }

  private syncBlackHoleFieldGeometry() {
    if (!this.snapshot || !this.blackHole) return;
    const singularity = this.snapshot.nodes.find((node) => node.archetype === "black-hole");
    if (!singularity) {
      this.blackHole.setFieldGeometry({ connectors: [] });
      return;
    }

    const nodesById = new Map(this.snapshot.nodes.map((node) => [node.id, node]));
    const startRadius = blackHoleConfig.diskOuterRadius + 4.8;
    const connectors = this.snapshot.edges
      .filter((edge) => edge.source === singularity.id || edge.target === singularity.id)
      .map((edge): BlackHoleConnectorField | null => {
        const counterpartId = edge.source === singularity.id ? edge.target : edge.source;
        const counterpart = nodesById.get(counterpartId);
        const route = this.snapshot?.projection.edges[edge.id];
        if (!counterpart || !route) return null;
        const counterpartPosition = this.world.getSemanticNodePosition(counterpartId);
        if (!counterpartPosition) return null;
        const [endX, endY] = this.raymarchField.fromSemantic(counterpartPosition);
        const length = Math.hypot(endX, endY);
        if (length < 0.001) return null;

        const physics = relationPhysics[edge.relation];
        const dirX = endX / length;
        const dirY = endY / length;
        const thickness = Math.max(0.078, blackHoleConfig.connectorWidth * (0.66 + edge.strength * 0.14));
        const opacity = Math.min(0.078, (0.02 + edge.strength * 0.016) * route.opacityScale * physics.opacityScale);
        const brightness = blackHoleConfig.connectorBrightness * (0.82 + edge.strength * 0.14);
        const curveSign = Math.sign(route.skew) || 1;
        const curvature =
          curveSign * Math.min(6.2, (1.4 + edge.strength * 2.2 + Math.abs(route.bend) * 16) * physics.bendScale);
        return {
          start: [dirX * startRadius, dirY * startRadius],
          end: [endX, endY],
          thickness,
          opacity,
          brightness,
          curvature,
        };
      })
      .filter((item): item is BlackHoleConnectorField => item !== null)
      .slice(0, 8);
    this.blackHole.setFieldGeometry({ connectors });
  }

  private readonly frame = (time: number) => {
    this.frameId = 0;
    if (!this.running || document.hidden) return;
    const delta = Math.min((time - this.lastTime) / 1000, 0.05);
    this.lastTime = time;
    this.elapsed += delta;
    const renderSample = this.renderScaleController.sample(time);
    if (renderSample) {
      this.host.dataset.renderFps = renderSample.fps.toFixed(0);
      this.host.dataset.renderScale = renderSample.scale.toFixed(2);
      if (renderSample.scaleChanged) this.resize();
    }

    this.world.updateSemantic(delta, this.elapsed);
    const semanticFocusPosition = this.world.getSemanticFocusPosition(this.focus);
    this.cameraDirector.updateFocusTarget(semanticFocusPosition);
    this.cameraDirector.update(delta);
    this.cameraDirector.camera.updateMatrixWorld(true);
    const singularity = this.world.getSingularitySemanticPosition();
    const semanticTarget = semanticFocusPosition ?? singularity;
    semanticZoomState(this.focus, this.cameraDirector.getSemanticCameraDistanceTo(semanticTarget), this.semanticZoom);
    this.world.setSemanticZoom(this.semanticZoom);
    this.world.updateVisual(delta, this.cameraDirector.camera, this.width, this.height);
    this.environment.update(delta, this.elapsed, this.idleMode);
    this.rareEvents.update(delta, this.elapsed);
    this.blackHole?.update(delta, this.cameraDirector.camera);

    if (this.renderer) {
      const blackHoleFocus = this.focusArchetype === "black-hole" && this.focus.kind !== "cosmos";
      const accessibleExposure = this.accessibleMode ? 1.05 : 1;
      const targetExposure = blackHoleFocus
        ? 0.88 * accessibleExposure
        : this.idleMode && !this.accessibleMode
          ? 0.96 + Math.sin(this.elapsed * 0.22) * 0.018
          : accessibleExposure;
      this.exposure += (targetExposure - this.exposure) * (1 - Math.exp(-delta * 2.4));
      this.renderer.toneMappingExposure = this.exposure;
    }

    const nodeId = focusedNodeId(this.focus);
    const orbitalId = focusedOrbitalId(this.focus);
    const related = this.world.getRelatedIds(nodeId);
    this.labels.update(
      this.cameraDirector.camera,
      this.width,
      this.height,
      delta,
      nodeId,
      orbitalId,
      this.hoveredEntityId,
      related,
      this.semanticZoom,
      this.accessibleMode,
    );
    this.postProcessing?.render();
    this.frameId = requestAnimationFrame(this.frame);
  };

  private readonly onVisibilityChange = () => {
    if (document.hidden) {
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
      return;
    }
    if (!this.running || this.frameId) return;
    this.lastTime = performance.now();
    this.renderScaleController.resetSampling();
    this.frameId = requestAnimationFrame(this.frame);
  };

  private resize() {
    if (!this.renderer) return;
    const width = Math.max(this.host.clientWidth, 1);
    const height = Math.max(this.host.clientHeight, 1);
    const nativePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const pixelRatio = Math.max(0.65, nativePixelRatio * this.renderScaleController.renderScale);
    const viewportChanged = width !== this.appliedViewportWidth || height !== this.appliedViewportHeight;
    const pixelRatioChanged = Math.abs(pixelRatio - this.appliedPixelRatio) > 0.001;
    this.width = width;
    this.height = height;
    if (!viewportChanged && !pixelRatioChanged) return;

    if (pixelRatioChanged) {
      this.renderer.setPixelRatio(pixelRatio);
      this.appliedPixelRatio = pixelRatio;
    }
    if (viewportChanged) {
      this.renderer.setSize(width, height, false);
      this.appliedViewportWidth = width;
      this.appliedViewportHeight = height;
      const coarsePointer = window.matchMedia?.("(pointer: coarse)").matches ?? false;
      const viewportFraming = this.cameraDirector.resize(width, height, coarsePointer);
      this.interactionPaddingScale = viewportFraming.hitPaddingScale;
      this.host.dataset.viewport = viewportFraming.compact ? "compact" : "full";
      this.host.dataset.orientation = viewportFraming.portrait ? "portrait" : "landscape";
      this.blackHole?.onResize(width, height);
    }
  }

  dispose() {
    this.running = false;
    delete this.host.dataset.engineReady;
    delete this.host.dataset.targetFps;
    delete this.host.dataset.renderFps;
    delete this.host.dataset.renderScale;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    cancelAnimationFrame(this.frameId);
    this.resizeObserver?.disconnect();
    this.pointer?.dispose();
    this.labels.dispose();
    this.world.dispose();
    this.environment.dispose(this.scene);
    this.rareEvents.dispose(this.scene);
    this.blackHole?.dispose();
    this.renderer?.dispose();
    this.renderer?.domElement.remove();
    this.renderer = null;
    this.postProcessing = null;
    this.blackHole = null;
  }
}
